import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, concatMap } from 'rxjs/operators';
import { AuthService, DatadogRumService, PcaService } from '../../services';
import { TaskType, TaskType as TaskTypeEnum } from '../vme-jobs/vme-jobs.component';
import { OrderQuoteService } from '../../services/order-quote.service';
import {
  format,
  getDaysInMonth,
  getDay,
  isAfter,
  addHours,
  isBefore,
  setMinutes,
  setHours,
  setDate,
  setYear,
  setMonth,
  parseISO,
  formatISO,
} from 'date-fns/fp';
import { OrderQuoteDTO } from '../../interfaces/order-quote';
import { QuoteAddressDTO } from '../../interfaces/quote-address';
import { DiscountCodeCheckResponse, OrderDiscountCode } from '../../interfaces/order-discount-code';
import { MarkerPoint } from '../map-control/marker.point';
import { compare } from 'fast-json-patch';
import { StripePaymentElementComponent, StripeService } from 'ngx-stripe';
import { StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { cloneDeep } from 'lodash';
import { OrderCreatedResponseDTO } from '../../interfaces/order-created-response';
import { BankersRoundPipe } from '../../pipes/bankers-round.pipe';
import { SelectItem } from 'primeng/api';
import { DiscountCodeService } from '../../services/discount-code.service';
import { postcodeValidator } from '../../utils/form-validators/postcode.validator';
import {
  AddressAutoCompletionService,
  SuggestionSource,
  UnifiedAddressSuggestion,
} from '../../services/address-auto-completion.service';
import { IVehicleAddress } from '../../interfaces/task-info';
import compose from 'lodash/fp/compose';
import { addDays, addMinutes, getHours } from 'date-fns';
import { vehicleKerbWeight } from '../create-movement-job/vehicle-kerb-weight-input/vehicle-kerb-weight-input.component';
import {
  B2cAddressQueryParams,
  B2cContactQueryParams,
  B2cQueryParams,
} from '../../interfaces/b2c-query-params';
import { CheckMotTax } from '../../utils/mot-tax-status.helper';
import { IsDisabledForDrivenMovementsVehicleBodyType } from '../../utils/is-disabled-for-driven-movements-vehicle-body-type';
import moment from 'moment';
import '@iframe-resizer/child';
import { datadogLogs } from '@datadog/browser-logs';

function invoiceEmailValidator(component: B2cWidgetComponent): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;

    // Perform validation logic
    if (component.useBillingEmailForInvoice) {
      return null;
    }

    if (controlValue == null || controlValue == '') {
      return { required: true };
    }

    return null;
  };
}

function collectionAddressValidator(component: B2cWidgetComponent): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;

    if (!control.touched) {
      return null;
    }

    // Perform validation logic
    if (component.quote.quotePickupAddress?.postcode) {
      return null;
    }

    return { unconfirmed: true };
  };
}

function deliveryAddressValidator(component: B2cWidgetComponent): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;

    if (!control.touched) {
      return null;
    }

    // Perform validation logic
    if (component.quote.quoteDropoffAddress?.postcode) {
      return null;
    }

    return { unconfirmed: true };
  };
}

const formatToIsoNoTime = format('yyyy-MM-dd');

const setZeroMinutesAndSeconds = (date: Date) => {
  const newDate = new Date(date);
  newDate.setMinutes(0);
  newDate.setSeconds(0);
  return newDate;
};

interface ICustomDateOption {
  dayOfMonth: number;
  price: number;
  date: string;
  isUrgent: boolean;
  showPrice: boolean;
  dateValue: Date;
  disabled: boolean;
}

@Component({
  selector: 'b2c-widget',
  templateUrl: 'b2c-widget.component.html',
  styleUrls: ['b2c-widget.component.scss'],
})
export class B2cWidgetComponent implements OnInit, OnDestroy {
  transportWindowHours = 4;
  tabShown = 1;
  step1: UntypedFormGroup;
  fetchingQuote = false;
  weeksInMonth: ICustomDateOption[][] = [];

  now = new Date();

  TaskType = TaskTypeEnum;

  quote: OrderQuoteDTO = {
    quoteVehicle: {},
    quotePickupAddress: {},
    quoteDropoffAddress: {},
    quoteBillingAddress: {},
    taskTypeId: TaskTypeEnum.DrivenPlated,
  };
  clientSecret: string = '';
  calendarMonth: number = 1;
  calendarYear: number = 1;
  pickupAddressForm: FormGroup;
  dropoffAddressForm: FormGroup;
  pickupContactForm: FormGroup;
  dropoffContactForm: FormGroup;
  minWorkingHour = 8;
  maxWorkingHour = 18;
  today: string = formatToIsoNoTime(this.now);
  vrm: string;
  pickupAddress: string;
  dropoffAddress: string;
  showAddress = false;
  addressIsPickup = true;
  editAddress: QuoteAddressDTO;
  customDate: Date;
  showDateDialog = false;

  useBillingEmailForInvoice: boolean = true;

  timeSlot: number = null;
  timeSlotOptions: SelectItem[] = [
    { label: 'Select Time', value: null },
    { label: '8am - midday', value: 8 },
    { label: '9am - 1pm', value: 9 },
    { label: '10am - 2pm', value: 10 },
    { label: '11am - 3pm', value: 11 },
    { label: 'midday - 4pm', value: 12 },
    { label: '1pm - 5pm', value: 13 },
    { label: '2pm - 6pm', value: 14 },
    { label: '3pm - 7pm', value: 15 },
    { label: '4pm - 8pm', value: 16 },
  ];

  leadTime = 'standard';
  bookUrgent = false;

  contactFormSubmitted = false;
  billingDetailsForm: FormGroup;
  billingFormSubmitted: boolean;
  showPaymentForm = false;
  showCallUsDialog = false;
  @ViewChild(StripePaymentElementComponent)
  paymentElement: StripePaymentElementComponent;
  stripeOptions: StripePaymentElementOptions = {
    fields: { billingDetails: { name: 'never', email: 'never', phone: 'never', address: 'never' } },
  };
  showMoreInfoDialog: boolean;
  validApiKey = true;
  invalidVRM = false;
  map: { pickup?: MarkerPoint; dropoff?: MarkerPoint } = {};
  private quoteState: OrderQuoteDTO;
  paying: boolean;
  private cardDetailsStatus: boolean;
  termsAgreed = false;
  orderResponse: OrderCreatedResponseDTO;
  discountCode = '';
  discountDialogVisible = false;

  discountDisplayText: string = '';
  discountCodeIsValid: boolean;
  discountDisplayTextIsValid: boolean;
  creatingPaymentIntent: boolean = false;
  pickupAddresses: any[] = [];
  dropoffAddresses: any[] = [];
  pickupPostcodeSearched: boolean = false;
  dropoffPostcodeSearched: boolean = false;
  selectedPickupAddress: any;
  selectedDropoffAddress: any;
  selectingPickupAddress: boolean = false;
  selectingDropoffAddress: boolean = false;
  orderDiscountCode: OrderDiscountCode;

  isOffshoreQuote = false;
  offshoreQuoteDialogVisible = false;
  isTransportedMovementDialogVisible = false;

  additionalVehicleInformationRequiredDialogVisible = false;
  kerbWeightDialogCancelled = false;
  manualKerbWeightValue: number | null = null;
  minKerbWeight = vehicleKerbWeight.minimum;
  maxKerbWeight = vehicleKerbWeight.maximum;
  isLoading = true;
  saveQuoteChangeSubject = new Subject<void>();
  noMatchedPickupAddress = false;
  noMatchedDropoffAddress = false;
  isInvalidMOTDialogVisible = false;
  isInvalidMOT = false;
  isMOTExpiring = false;
  isUnableToBookDrivenMotorcycleDialogVisible = false;
  selectedCustomDay: ICustomDateOption;

  constructor(
    private formBuilder: FormBuilder,
    private orderQuoteService: OrderQuoteService,
    private authService: AuthService,
    private pcaService: PcaService,
    private stripeService: StripeService,
    private discountCodeService: DiscountCodeService,
    private addressAutoCompleteService: AddressAutoCompletionService,
    private route: ActivatedRoute,
    private router: Router,
    private datadogRumService: DatadogRumService,
    private bankersRoundPipe: BankersRoundPipe,
  ) {
    this.calendarYear = this.now.getFullYear();
    this.calendarMonth = this.now.getMonth();
    this.calendarMonthName = format(
      'MMMM',
      this.getDateFromMonthYear(this.calendarMonth, this.calendarYear),
    );
  }

  public calendarMonthName: string;

  get dcf() {
    return this.dropoffContactForm?.value;
  }

  get pcf() {
    return this.pickupContactForm?.value;
  }

  async ngOnInit() {
    await this.datadogRumService.init(true);
    window.iFrameResizer = {
      targetOrigin: '*',
      onReady: () => console.log('iframe-resizer is ready'),
    };

    await this.getAPIKey();

    this.saveQuoteChangeSubject
      .pipe(
        debounceTime(500),
        concatMap(() => this.saveQuoteChanges()),
      )
      .subscribe(() => {
        this.fetchQuote();
      });

    this.route.queryParamMap.subscribe(async (queryParams) => {
      if (queryParams.keys.length === 0) {
        this.isLoading = false;
        return;
      }

      try {
        const params = this.parseQueryParams(queryParams);
        await this.handleQueryParams(params);
      } catch (ex) {
        console.log('Error handling query params', ex);
      } finally {
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.saveQuoteChangeSubject.complete();
  }

  async getAPIKey() {
    const { apiKey } = await this.authService.getBookingToolAPIKey();

    if (apiKey == null || apiKey.length == 0) {
      this.validApiKey = false;
      return;
    }

    await this.authService.refreshAPIAuthToken(apiKey);

    this.initPCA();
    this.buildForms();
  }

  buildForms() {
    this.step1 = this.formBuilder.group({
      taskType: [1, Validators.required],
      vrm: ['', Validators.required],
      pickupPostcode: ['', postcodeValidator()],
      dropoffPostcode: ['', postcodeValidator()],
    });

    this.pickupAddressForm = this.formBuilder.group({
      pickupAddress: [null, collectionAddressValidator(this)],
      businessName: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      town: [''],
      city: [''],
      postcode: ['', Validators.required],
      lat: [],
      lng: [],
    });

    this.dropoffAddressForm = this.formBuilder.group({
      dropoffAddress: [null, deliveryAddressValidator(this)],
      businessName: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      town: [''],
      city: [''],
      postcode: ['', Validators.required],
      lat: [],
      lng: [],
    });

    this.pickupContactForm = this.formBuilder.group({
      forename: ['', Validators.required],
      surname: ['', Validators.required],
      email: ['', [Validators.required, emailValidator]],
      phone1: ['', [Validators.required, phoneNumberValidator]],
      notes: [''],
      isPrivate: [true],
    });

    this.dropoffContactForm = this.formBuilder.group({
      forename: ['', Validators.required],
      surname: ['', Validators.required],
      email: ['', [Validators.required, emailValidator]],
      phone1: ['', [Validators.required, phoneNumberValidator]],
      notes: [''],
      isPrivate: [true],
    });

    this.billingDetailsForm = this.formBuilder.group({
      forename: ['', Validators.required],
      phone1: ['', [Validators.required, phoneNumberValidator]],
      businessName: [''],
      email: ['', [Validators.required, emailValidator]],
      invoiceEmail: ['', [invoiceEmailValidator(this), emailValidator]],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      town: [''],
      city: [''],
      postcode: [''],
      termsAgreed: [false, Validators.requiredTrue],
    });
  }

  getDateFromMonthYear(month: number, year: number) {
    return compose(setYear(year), setMonth(month), setDate(1))(this.now);
  }

  priceAvailableForDay(date: Date) {
    let minDays: number;

    if (getDay(this.now) == 6) {
      minDays = 3;
    } else if (this.quote.taskTypeId == TaskTypeEnum.DrivenPlated) {
      minDays = 2;
    } else {
      minDays = 3;
    }

    return !isBefore(addDays(this.now, minDays), date);
  }

  initMonth(month: number, year: number) {
    const startDate = this.getDateFromMonthYear(month, year);
    const daysInMonth = getDaysInMonth(startDate);

    let currentWeek: ICustomDateOption[] = [];
    this.weeksInMonth = [];

    for (let i = 1; i <= daysInMonth; i++) {
      const weekDay = getDay(setDate(i, startDate));

      if (i == 1 && weekDay != 0) {
        for (let j = 0; j < weekDay; j++) {
          currentWeek.push(null);
        }
      }

      const monthDay = setDate(i, startDate);

      const urgentEnds = this.addWorkingDays(this.now, this.quote.urgentHours / 24);

      const isUrgent = !isAfter(urgentEnds, monthDay);

      const showPrice = this.priceAvailableForDay(monthDay);

      currentWeek.push({
        dayOfMonth: i,
        price: isUrgent ? this.quote.urgentPrice : this.quote.standardPrice,
        date: formatToIsoNoTime(monthDay),
        isUrgent: isUrgent,
        showPrice,
        dateValue: monthDay,
        disabled: !showPrice,
      });

      if (weekDay == 6 || i == daysInMonth) {
        this.weeksInMonth.push(currentWeek);
        currentWeek = [];
      }
    }

    console.log(this.weeksInMonth);
  }

  fetchQuote() {
    if (!this.canShowDateChoices) {
      // We need more information to fetch a quote
      return false;
    }

    this.fetchingQuote = true;
    this.isOffshoreQuote = false;

    this.orderQuoteService
      .generateQuote(this.quote.quoteGuid)
      .then((quote) => {
        this.fetchingQuote = false;
        this.quote = quote;
        this.quote.bookUrgent = this.bookUrgent;
        this.quote.standardDiscountAmount = 0;
        this.quote.urgentDiscountAmount = 0;
        this.quote.discountCode = null;
        this.discountCode = '';
        this.storeQuoteState();

        if (quote?.isOffshoreQuote) {
          this.isOffshoreQuote = true;
          this.offshoreQuoteDialogVisible = true;
        }

        if (quote?.quoteSuccess == true) {
          this.initMonth(this.now.getMonth(), this.now.getFullYear());
          this.initAddresses();
          this.calculatePickupDropoffByWithinDays(7);
        }
      })
      .catch((error) => {
        console.log('Something went wrong ', error);
      });
  }

  async vrmLookup() {
    this.invalidVRM = false;
    this.kerbWeightDialogCancelled = false;

    try {
      const quoteVehicle = await this.orderQuoteService.vrmLookup(this.vrm);

      if (!quoteVehicle) {
        console.log('No quote vehicle found');
        return;
      }

      if (quoteVehicle.make != null) {
        delete quoteVehicle.id;

        const isSameVehicle =
          this.quote.quoteVehicle?.registrationNumber?.toUpperCase() ===
          quoteVehicle.registrationNumber?.toUpperCase();
        const existingKerbWeight = isSameVehicle ? this.quote.quoteVehicle?.kerbWeight : null;

        this.quote.quoteVehicle = Object.assign(this.quote.quoteVehicle, quoteVehicle);
        this.quote.quoteVehicle.registrationNumber = quoteVehicle.registrationNumber?.toUpperCase();

        if (existingKerbWeight && (!quoteVehicle.kerbWeight || quoteVehicle.kerbWeight == 0)) {
          this.quote.quoteVehicle.kerbWeight = existingKerbWeight;
        }

        if (this.quoteState?.quoteVehicle) {
          this.quoteState.quoteVehicle.summary = quoteVehicle.summary;
        }

        const needsKerbWeight =
          !this.quote.quoteVehicle.kerbWeight || this.quote.quoteVehicle.kerbWeight == 0;

        if (needsKerbWeight) {
          this.additionalVehicleInformationRequiredDialogVisible = true;
        } else {
          this.saveQuoteChangeSubject.next();
        }
      } else {
        this.invalidVRM = true;
      }
    } catch (ex) {
      this.invalidVRM = true;
    }
  }

  addressChange(event: any) {
    if (event.country && event.country !== 'United Kingdom') {
      this.isOffshoreQuote = true;
      this.offshoreQuoteDialogVisible = true;
      this.showAddress = false;
      return;
    }

    if (this.addressIsPickup) {
      this.pickupAddress = event.primaryAddress;
      this.noMatchedPickupAddress = false;
      this.selectingPickupAddress = false;
      delete event.primaryAddress;
      this.pickupAddressForm.patchValue(event);
      this.quote.quotePickupAddress = Object.assign(this.quote.quotePickupAddress, event);
      this.map.pickup = new MarkerPoint(event.latitude, event.longitude);
      this.pickupAddressForm.controls.pickupAddress.updateValueAndValidity();
    } else {
      this.dropoffAddress = event.primaryAddress;
      this.noMatchedDropoffAddress = false;
      this.selectingDropoffAddress = false;
      delete event.primaryAddress;
      this.dropoffAddressForm.patchValue(event);
      this.quote.quoteDropoffAddress = Object.assign(this.quote.quoteDropoffAddress, event);
      this.map.dropoff = new MarkerPoint(event.latitude, event.longitude);
      this.dropoffAddressForm.controls.dropoffAddress.updateValueAndValidity();
    }

    this.saveQuoteChangeSubject.next();
  }

  setShowAddress($event: any) {
    this.showAddress = $event;
  }

  get canShowDateChoices() {
    return (
      this.invalidVRM == false &&
      this.quote?.taskTypeId != null &&
      this.quote?.quoteVehicle?.registrationNumber?.length > 0 &&
      this.quote?.quotePickupAddress?.postcode?.length > 0 &&
      this.quote?.quoteDropoffAddress?.postcode?.length > 0 &&
      this.quote?.urgentHours != null
    );
  }

  showRoute() {
    return this.quote?.quotePickupAddress?.latitude && this.quote?.quoteDropoffAddress?.latitude;
  }

  get showPrice() {
    return this.canShowDateChoices && this.quote?.standardPrice > 0;
  }

  isDateInUrgentWindow(quoteDate: Date, urgentHours: number) {
    let endOfUrgentWindow = addHours(urgentHours, this.now);

    endOfUrgentWindow = addMinutes(endOfUrgentWindow, 1);

    return quoteDate < endOfUrgentWindow;
  }

  addWorkingDays(startDate, daysToAdd) {
    let currentDate = new Date(startDate);
    while (daysToAdd > 0) {
      currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        // Skip weekends (0: Sunday, 6: Saturday)
        daysToAdd--;
      }
    }
    return currentDate;
  }

  subtractWorkingDays(startDate, daysToAdd) {
    let currentDate = new Date(startDate);
    while (daysToAdd > 0) {
      currentDate.setDate(currentDate.getDate() - 1); // Move to the next day
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        // Skip weekends (0: Sunday, 6: Saturday)
        daysToAdd--;
      }
    }
    return currentDate;
  }

  setLeadTime(leadTime: string) {
    this.leadTime = leadTime;
    const hours = leadTime == 'urgent' ? this.quote.urgentHours : 7 * 24;
    const days = hours / 24;
    this.bookUrgent = leadTime == 'urgent';
    this.quote.bookUrgent = leadTime == 'urgent';
    this.calculatePickupDropoffByWithinDays(days);
  }

  calculatePickupDropoffByWithinDays(days: number) {
    const durationHours = Math.ceil(this.quote.duration / 3600) || 0;

    let pickupEarliest = this.addWorkingDays(this.now, 1);
    pickupEarliest = setHours(this.minWorkingHour, pickupEarliest);

    let dropOffLatest = this.addWorkingDays(pickupEarliest, days - 1);
    dropOffLatest = setHours(this.maxWorkingHour, dropOffLatest);

    let dropOffEarliest = addHours(durationHours, pickupEarliest);
    dropOffEarliest = addHours(2, dropOffEarliest);

    if (getHours(dropOffEarliest) > this.maxWorkingHour) {
      dropOffEarliest = addDays(dropOffEarliest, 1);
      dropOffEarliest = setHours(this.minWorkingHour, dropOffEarliest);
    }

    let pickupLatest = addHours(-1 * durationHours, dropOffLatest);
    pickupLatest = addHours(-1, pickupLatest);

    if (getHours(pickupLatest) < this.minWorkingHour) {
      pickupLatest = this.subtractWorkingDays(pickupLatest, 1);
      pickupLatest = setHours(18, pickupLatest);
    }

    pickupEarliest = setZeroMinutesAndSeconds(pickupEarliest);
    pickupLatest = setZeroMinutesAndSeconds(pickupLatest);
    dropOffEarliest = setZeroMinutesAndSeconds(dropOffEarliest);
    dropOffLatest = setZeroMinutesAndSeconds(dropOffLatest);

    this.quote.pickupDateTimeEarliest = formatISO(pickupEarliest);
    this.quote.pickupDateTimeLatest = formatISO(pickupLatest);
    this.quote.dropoffDateTimeEarliest = formatISO(dropOffEarliest);
    this.quote.dropoffDateTimeLatest = formatISO(dropOffLatest);
  }

  calculatePickupDropoffCustomDate() {
    // Default to 8am pickup
    this.timeSlot = this.timeSlot == null ? 8 : this.timeSlot;

    let pickupEarliest = setHours(this.timeSlot, this.customDate);
    pickupEarliest = setMinutes(0, pickupEarliest);

    let pickupLatest = addHours(this.transportWindowHours, pickupEarliest);

    const durationHours = Math.ceil(this.quote.duration / 3600) || 1;

    let dropOffEarliest = addHours(durationHours, pickupEarliest);

    let dropOffLatest = addHours(this.transportWindowHours, dropOffEarliest);

    if (getHours(dropOffLatest) > this.maxWorkingHour) {
      dropOffEarliest = addDays(dropOffEarliest, 1);
      dropOffEarliest = setHours(this.minWorkingHour, dropOffEarliest);
      dropOffLatest = addHours(this.transportWindowHours, dropOffEarliest);
    }

    pickupEarliest = setZeroMinutesAndSeconds(pickupEarliest);
    pickupLatest = setZeroMinutesAndSeconds(pickupLatest);
    dropOffEarliest = setZeroMinutesAndSeconds(dropOffEarliest);
    dropOffLatest = setZeroMinutesAndSeconds(dropOffLatest);

    this.quote.pickupDateTimeEarliest = formatISO(pickupEarliest);
    this.quote.pickupDateTimeLatest = formatISO(pickupLatest);
    this.quote.dropoffDateTimeEarliest = formatISO(dropOffEarliest);
    this.quote.dropoffDateTimeLatest = formatISO(dropOffLatest);

    return;
  }

  setTaskType(taskType: TaskTypeEnum) {
    if (this.quote != null) {
      this.quote.taskTypeId = taskType;

      if (taskType == TaskType.UncoveredTransportedMovement) {
        this.isTransportedMovementDialogVisible = true;
      }

      this.saveQuoteChanges().then(() => {
        this.fetchQuote();
      });
    }
  }

  showSpecificDateDialog() {
    this.initMonth(new Date().getMonth(), new Date().getFullYear());
    this.showDateDialog = true;
    this.leadTime = 'custom';
  }

  setCustomDateSelected(day: ICustomDateOption) {
    if (!day || day.disabled || day?.date == this.today) {
      return;
    }
    this.customDate = day.dateValue;
    this.selectedCustomDay = day;
    this.calculatePickupDropoffCustomDate();
  }

  confirmCustomDate() {
    this.calculatePickupDropoffCustomDate();
    this.setLeadTimeForCustomDate();
    this.showDateDialog = false;
  }

  private setLeadTimeForCustomDate() {
    const urgentEnds = addHours(this.quote.urgentHours, this.now);
    const isUrgent = !isAfter(urgentEnds, parseISO(this.quote.dropoffDateTimeEarliest));
    this.bookUrgent = isUrgent;
    this.quote.bookUrgent = isUrgent;
    this.leadTime = 'custom';
  }

  setIsPrivate(isPickup: boolean, isPrivate: boolean) {
    if (isPickup) {
      this.pickupContactForm.patchValue({ isPrivate: isPrivate });
    } else {
      this.dropoffContactForm.patchValue({ isPrivate: isPrivate });
    }
  }

  get specificDateLatest() {
    if (!this.quote.pickupDateTimeEarliest) {
      return this.now;
    }

    return addHours(this.transportWindowHours, parseISO(this.quote.pickupDateTimeEarliest));
  }

  saveContactDetails() {
    this.contactFormSubmitted = true;

    this.pickupContactForm.markAllAsTouched();
    this.dropoffContactForm.markAllAsTouched();

    if (this.pickupContactForm.invalid || this.dropoffContactForm.invalid) {
      return;
    }

    this.quote.quotePickupAddress = Object.assign(
      this.quote.quotePickupAddress,
      this.pickupContactForm.value,
    );

    this.quote.quoteDropoffAddress = Object.assign(
      this.quote.quoteDropoffAddress,
      this.dropoffContactForm.value,
    );

    this.saveQuoteChanges().then(() => {
      this.tabShown = 3;
    });
  }

  async saveBillingDetails() {
    if (this.useBillingEmailForInvoice) {
      this.billingDetailsForm.controls.invoiceEmail.setValue(
        this.billingDetailsForm.controls.email.value,
      );
    }

    this.billingDetailsForm.markAllAsTouched();
    this.billingFormSubmitted = true;

    if (this.billingDetailsForm.invalid) {
      return;
    }

    void this.goToTab(4);

    const { termsAgreed, ...quoteBillingAddress } = this.billingDetailsForm.value;

    this.quote.quoteBillingAddress = {
      ...quoteBillingAddress,
      id: this.quote.quoteBillingAddress.id,
      summary: '',
    };

    datadogLogs.setUser({
      email: this.quote.quoteBillingAddress.email,
    });

    await this.saveQuoteChanges();

    this.creatingPaymentIntent = true;

    try {
      const response = await this.orderQuoteService.createPaymentIntent(this.quote.quoteGuid);
      this.billingFormSubmitted = false;

      if (response.success) {
        this.quote.paymentIntentId = response.paymentIntentId;
        this.clientSecret = response.clientSecret;
        this.showPaymentForm = true;
      } else {
      }
    } catch (err) {
      datadogLogs.logger.error('Error creating payment intent', { error: err });
    } finally {
      this.creatingPaymentIntent = false;
    }
  }

  async saveQuoteChanges() {
    // If we have never saved this quote before, create it
    if (this.quote?.quoteGuid == null) {
      this.setLeadTime('standard');

      const orderQuote = await this.orderQuoteService.initializeQuote({
        taskTypeId: this.quote.taskTypeId,
        quoteVehicle: this.quote.quoteVehicle,
        quotePickupAddress: this.quote.quotePickupAddress,
        quoteDropoffAddress: this.quote.quoteDropoffAddress,
        quoteBillingAddress: this.quote.quoteBillingAddress,
        pickupDateTimeEarliest: this.quote.pickupDateTimeEarliest,
        pickupDateTimeLatest: this.quote.pickupDateTimeLatest,
        dropoffDateTimeEarliest: this.quote.dropoffDateTimeEarliest,
        dropoffDateTimeLatest: this.quote.dropoffDateTimeLatest,
      });
      this.quote = cloneDeep(orderQuote);
      this.storeQuoteState();
    }

    const quotePatch = compare(this.quoteState, this.quote);

    // Remove unnecessary properties
    // 1. ClientSecret
    // 2. Remove DateFormat fields, since we'll be using date string versions

    if (quotePatch.length > 0) {
      await this.orderQuoteService.patchQuote(this.quote.quoteGuid, quotePatch);
      this.storeQuoteState();
    }

    return Promise.resolve();
  }

  showEmailError(form: FormGroup, email: string, formSubmitted: boolean) {
    return (
      form.controls[email].hasError('notEmail') && (formSubmitted || form.controls[email].touched)
    );
  }

  showPhoneError(form: FormGroup, email: string, formSubmitted: boolean) {
    return (
      form.controls[email].hasError('notPhoneNumber') &&
      (formSubmitted || form.controls[email].touched)
    );
  }

  pay() {
    if (this.paying) {
      return false;
    }

    this.paying = true;

    this.stripeService
      .confirmPayment({
        elements: this.paymentElement.elements,
        redirect: 'if_required',
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name:
                this.quote.quoteBillingAddress?.forename && this.quote.quoteBillingAddress?.surname
                  ? `${this.quote.quoteBillingAddress.forename} ${this.quote.quoteBillingAddress.surname}`
                  : 'Anonymous',
              email: this.quote.quoteBillingAddress.email,
              phone: this.quote.quoteBillingAddress.phone1,
              address: {
                line1: this.quote.quoteBillingAddress.addressLine1,
                line2: this.quote.quoteBillingAddress.addressLine2,
                state:
                  this.quote.quoteBillingAddress.city ?? this.quote.quoteBillingAddress.town ?? '',
                city:
                  this.quote.quoteBillingAddress.town ?? this.quote.quoteBillingAddress.city ?? '',
                postal_code: this.quote.quoteBillingAddress.postcode,
                country: 'GB',
              },
            },
          },
        },
      })
      .subscribe((result) => {
        if (result.error) {
          datadogLogs.logger.error('Payment processing error', { error: result.error });
          this.showMoreInfoDialog = true;
        } else {
          // The payment has been processed!
          if (result.paymentIntent.status === 'succeeded') {
            this.orderQuoteService
              .paymentMade(this.quote.quoteGuid, result.paymentIntent.id)
              .then((response) => {
                const isIframe = window.self !== window.top;
                console.log('Is iFrame', isIframe);
                if (response.order_number == null) {
                  this.showMoreInfoDialog = true;
                } else {
                  if (isIframe) {
                    const netPrice = this.quote.bookUrgent
                      ? this.quote.urgentPrice
                      : this.quote.standardPrice;

                    const grossPrice = this.bankersRoundPipe.transform(netPrice * 1.2, 2);

                    //Bubble revenue info up for GA4 tracking.
                    window.parent.postMessage(
                      {
                        type: 'b2c-checkout',
                        quoteId: this.quote.quoteGuid,
                        transactionId: response.order_number,
                        value: grossPrice,
                        netValue: netPrice,
                        tax: this.bankersRoundPipe.transform(grossPrice - netPrice, 2),
                        currency: 'GBP',
                        movementType: this.quote.taskType?.name,
                      },
                      '*',
                    );
                  } else {
                    this.router.navigate([`/b2c/confirmation/${this.quote.quoteGuid}`]);
                  }
                }
              });
          } else if (result.paymentIntent.status === 'processing') {
            datadogLogs.logger.warn('Payment processing issue', {
              status: result.paymentIntent.status,
            });
          }
        }
      });
  }

  canSaveBillingDetails() {
    return this.billingDetailsForm.valid && this.clientSecret == '' && this.termsAgreed;
  }

  paymentChange($event: StripePaymentElementChangeEvent) {
    this.cardDetailsStatus = $event.complete;
  }

  canSubmitPayment() {
    return this.cardDetailsStatus;
  }

  async goToTab(number: number) {
    if (number == 1) {
      this.tabShown = 1;
    }

    if (number == 2 && this.showPrice) {
      this.clientSecret = '';
      this.tabShown = 2;
    }

    if (number == 3 && this.tabShown == 2) {
      this.saveContactDetails();
      return;
    }

    if (number == 3 && this.tabShown == 3) {
      await this.saveBillingDetails();
    }

    if (number == 4 && this.tabShown == 3) {
      this.tabShown = 4;
    }
  }

  canAcceptCustomDate() {
    if (this.selectedCustomDay?.disabled) {
      return false;
    }
    return this.customDate != null && this.timeSlot != null;
  }

  urgentEnds() {
    const addDays = Math.ceil(this.quote.urgentHours / 24);
    return this.addWorkingDays(new Date(), addDays);
  }

  standardEnds() {
    return this.addWorkingDays(new Date(), 7);
  }

  changeMonth(number: number) {
    this.calendarMonth += number;
    if (this.calendarMonth > 11) {
      this.calendarMonth = 0;
      this.calendarYear++;
    }
    if (this.calendarMonth < 0) {
      this.calendarMonth = 11;
      this.calendarYear--;
    }
    this.calendarMonthName = format(
      'MMMM',
      this.getDateFromMonthYear(this.calendarMonth, this.calendarYear),
    );
    this.initMonth(this.calendarMonth, this.calendarYear);
  }

  isDisabledDay(day) {
    const date = new Date(day?.dateValue);
    return date <= this.now;
  }

  isDateChosen(day) {
    return (
      day != null &&
      this.customDate != null &&
      new Date(day.date).toDateString() == this.customDate.toDateString()
    );
  }

  private initAddresses() {
    this.pickupAddressForm.patchValue({
      businessName: this.quote.quotePickupAddress.businessName,
      addressLine1: this.quote.quotePickupAddress.addressLine1,
      addressLine2: this.quote.quotePickupAddress.addressLine2,
      town: this.quote.quotePickupAddress.town,
      city: this.quote.quotePickupAddress.city,
      postcode: this.quote.quotePickupAddress.postcode,
    });

    this.dropoffAddressForm.patchValue({
      businessName: this.quote.quoteDropoffAddress.businessName,
      addressLine1: this.quote.quoteDropoffAddress.addressLine1,
      addressLine2: this.quote.quoteDropoffAddress.addressLine2,
      town: this.quote.quoteDropoffAddress.town,
      city: this.quote.quoteDropoffAddress.city,
      postcode: this.quote.quoteDropoffAddress.postcode,
    });
  }

  private initPCA() {
    this.pcaService.Init().then(() => {
      // this.initPickupAddress();
      // this.initDropoffAddress();
    });
  }

  private storeQuoteState() {
    this.quoteState = cloneDeep(this.quote);
  }

  async applyDiscountCode() {
    console.log('applying discount code');
    let checkResponse: DiscountCodeCheckResponse = {
      isValid: false,
      message: '',
      orderDiscountCode: null,
    };
    try {
      checkResponse = await this.discountCodeService.checkDiscountCode(this.discountCode);
    } catch (error) {
      console.log(error);
      this.discountDisplayText = 'Invalid discount code';
      this.discountDisplayTextIsValid = false;
    }

    if (!checkResponse.isValid) {
      console.log(checkResponse);
      this.discountDisplayText = checkResponse.message;
      this.discountDisplayTextIsValid = checkResponse.isValid;
      return;
    }

    if (!checkResponse.orderDiscountCode?.urgent && this.quote.bookUrgent) {
      this.discountDisplayText = 'Discount code cannot be applied to urgent quotes';
      this.discountDisplayTextIsValid = false;
      return;
    }

    if (!checkResponse.orderDiscountCode?.nonUrgent && !this.quote.bookUrgent) {
      this.discountDisplayText = 'Discount code cannot be applied to non-urgent quotes';
      this.discountDisplayTextIsValid = false;
      return;
    }

    this.orderDiscountCode = checkResponse.orderDiscountCode;

    try {
      const result = await this.discountCodeService.applyDiscountCode(
        this.discountCode,
        this.quote.quoteGuid,
        this.quote.bookUrgent,
      );
      this.quote.standardPrice = result.standardPrice;
      this.quote.urgentPrice = result.urgentPrice;
      this.quote.urgentDiscountAmount = result.urgentDiscountAmount;
      this.quote.standardDiscountAmount = result.standardDiscountAmount;
      this.quote.discountCode = this.discountCode;
      this.storeQuoteState();
      this.discountCodeIsValid = checkResponse.isValid;
      this.discountDialogVisible = false;
    } catch (error) {
      this.discountDisplayTextIsValid = false;
      this.discountDisplayText = 'Unable to apply discount code';
    }
  }

  showDiscountCodeDialog() {
    this.discountDialogVisible = true;
  }

  async searchPostcodeAddress(isPickup: boolean) {
    const valid = isPickup
      ? this.step1.controls.pickupPostcode.valid
      : this.step1.controls.dropoffPostcode.valid;

    if (!valid) {
      return;
    }

    const rawPostcode = isPickup
      ? this.step1.value.pickupPostcode
      : this.step1.value.dropoffPostcode;
    const postcode = (rawPostcode || '').trim();

    const autoCompleteSuggestions = await this.addressAutoCompleteService
      .getSuggestions(postcode)
      .toPromise();
    const loqateSuggestions = await this.addressAutoCompleteService
      .getSuggestionsFromLoqate(postcode)
      .toPromise();

    const filteredLoqateSuggestions = this.addressAutoCompleteService.filterLoqateSuggestions(
      autoCompleteSuggestions,
      loqateSuggestions,
    );

    const unifiedSuggestions = this.addressAutoCompleteService.transformSuggestions(
      autoCompleteSuggestions,
      filteredLoqateSuggestions,
    );

    console.log(unifiedSuggestions);

    if (isPickup) {
      this.pickupAddresses = unifiedSuggestions;
      this.pickupPostcodeSearched = true;
      this.selectingPickupAddress = true;
    } else {
      this.dropoffAddresses = unifiedSuggestions;
      this.dropoffPostcodeSearched = true;
      this.selectingDropoffAddress = true;
    }
  }

  async onSelectedAddressChange(
    $event: { value: UnifiedAddressSuggestion },
    isPickup: boolean,
    showAddressConfirmation = true,
  ) {
    const selectedAddress = $event.value;

    let vehicleAddress: IVehicleAddress;

    if (selectedAddress.source == SuggestionSource.Main) {
      vehicleAddress = await this.addressAutoCompleteService
        .getAddressDetails(selectedAddress.id)
        .toPromise()
        .then((response) => this.addressAutoCompleteService.convertToVehicleAddress(response));
    } else {
      vehicleAddress = await this.addressAutoCompleteService
        .getLoqateAddressDetails(selectedAddress.id)
        .toPromise()
        .then((response) =>
          this.addressAutoCompleteService.convertLoqateToVehicleAddress(response.Items[0]),
        );
    }

    console.log(vehicleAddress);

    this.editAddress = vehicleAddress;
    this.showAddress = showAddressConfirmation;
    this.addressIsPickup = isPickup;
  }

  limitKerbWeight(event: any): void {
    const input = event.target;
    input.value = input.value.replace(/[^0-9.]/g, '');
    this.manualKerbWeightValue = input.value ? parseFloat(input.value) : null;
  }

  canContinueWithManualKerbWeight(): boolean {
    return (
      this.manualKerbWeightValue != null &&
      this.manualKerbWeightValue >= this.minKerbWeight &&
      this.manualKerbWeightValue <= this.maxKerbWeight
    );
  }

  continueWithManualKerbWeightEntry(): void {
    if (!this.canContinueWithManualKerbWeight()) {
      return;
    }

    if (this.quote?.quoteVehicle) {
      this.quote.quoteVehicle.kerbWeight = this.manualKerbWeightValue;
      this.additionalVehicleInformationRequiredDialogVisible = false;

      this.saveQuoteChanges().then(() => {
        this.fetchQuote();
      });
    }
  }

  onKerbWeightDialogClosed(): void {
    if (!this.canContinueWithManualKerbWeight()) {
      this.quote.quoteVehicle = {};
      this.vrm = '';
      this.manualKerbWeightValue = null;
      this.invalidVRM = false;
      this.kerbWeightDialogCancelled = true;

      if (this.step1) {
        this.step1.controls.vrm.setValue('');
      }
    } else {
      this.kerbWeightDialogCancelled = false;
    }

    this.manualKerbWeightValue = null;
  }

  handleTransportedMovementDialog(isAccepted: boolean): void {
    if (!isAccepted) {
      this.setTaskType(TaskType.DrivenPlated);
      this.showCallUsDialog = true;
    }

    this.isTransportedMovementDialogVisible = false;
  }

  handleEnterDetails() {
    if (this.quote?.taskTypeId !== TaskType.DrivenPlated) {
      void this.goToTab(2);
      return;
    }

    if (IsDisabledForDrivenMovementsVehicleBodyType(this.quote?.quoteVehicle?.body)) {
      this.isUnableToBookDrivenMotorcycleDialogVisible = true;
      return;
    }

    if (!this.isMOTValid()) {
      this.isInvalidMOTDialogVisible = true;
      return;
    }

    void this.goToTab(2);
  }

  handleInvalidMOTDialog(isConfirmed: boolean) {
    if (isConfirmed) {
      this.quote.quoteVehicle.hasCustomerAcceptedInvalidMot = true;

      this.saveQuoteChanges().then(() => {
        this.isInvalidMOTDialogVisible = false;
        void this.goToTab(2);
      });
    } else {
      this.isInvalidMOTDialogVisible = false;
      this.quote.quoteVehicle.hasCustomerAcceptedInvalidMot = false;
      this.setTaskType(TaskType.UncoveredTransportedMovement);
    }
  }

  reload() {
    window.location.reload();
  }

  private isMOTValid() {
    this.isInvalidMOT = false;
    this.isMOTExpiring = false;

    const motTaxStatus = CheckMotTax(this.quote?.quoteVehicle);

    if (!motTaxStatus.isMotValid) {
      this.isInvalidMOT = true;

      return false;
    } else if (
      moment(this.quote?.quoteVehicle?.motExpiryDate) <=
      moment(new Date(this.quote?.pickupDateTimeEarliest))
    ) {
      this.isMOTExpiring = true;

      return false;
    }

    return true;
  }

  private parseQueryParams(params: ParamMap) {
    const queryParams: B2cQueryParams = {
      collectionContact: {
        email: params.get('collectionContactEmail'),
        name: params.get('collectionContactName'),
        phone: params.get('collectionContactPhone'),
        surname: params.get('collectionContactSurname'),
        notes: params.get('collectionNotes'),
        isPrivate:
          params.get('isCollectionAddressPrivate') === null
            ? null
            : params.get('isCollectionAddressPrivate').toLowerCase() === 'true',
      },
      deliveryContact: {
        email: params.get('deliveryContactEmail'),
        name: params.get('deliveryContactName'),
        phone: params.get('deliveryContactPhone'),
        surname: params.get('deliveryContactSurname'),
        notes: params.get('deliveryNotes'),
        isPrivate:
          params.get('isDeliveryAddressPrivate') === null
            ? null
            : params.get('isDeliveryAddressPrivate').toLowerCase() === 'true',
      },
      dropoffAddress: {
        addressLine1: params.get('dropoffAddress_1'),
        addressLine2: params.get('dropoffAddress_2'),
        addressLine3: params.get('dropoffAddress_3'),
        town: params.get('dropoffTown'),
        county: params.get('dropoffCounty'),
        postcode: (params.get('dropoffPostcode') ?? '').trim(),
      },
      pickupAddress: {
        addressLine1: params.get('pickupAddress_1'),
        addressLine2: params.get('pickupAddress_2'),
        addressLine3: params.get('pickupAddress_3'),
        town: params.get('pickupTown'),
        county: params.get('pickupCounty'),
        postcode: (params.get('pickupPostcode') ?? '').trim(),
      },
      vehicleRegistration: params.get('vehicleRegistration'),
    };

    return queryParams;
  }

  private async handleQueryParams(params: B2cQueryParams) {
    if (params.vehicleRegistration) {
      this.vrm = params.vehicleRegistration;
      await this.vrmLookup();
    }

    if (params.pickupAddress.postcode) {
      this.step1.get('pickupPostcode').patchValue(params.pickupAddress.postcode);
      this.step1.get('pickupPostcode').markAsTouched();

      await this.searchPostcodeAddress(true);

      if (this.pickupAddresses.length > 0) {
        await this.setAddressFromQueryParams(params.pickupAddress, this.pickupAddresses, true);
      }
    }

    if (params.dropoffAddress.postcode) {
      this.step1.get('dropoffPostcode').patchValue(params.dropoffAddress.postcode);
      this.step1.get('dropoffPostcode').markAsTouched();

      await this.searchPostcodeAddress(false);

      if (this.dropoffAddresses.length > 0) {
        await this.setAddressFromQueryParams(params.dropoffAddress, this.dropoffAddresses, false);
      }
    }

    this.setContactDetailsFromQueryParams(params.collectionContact, this.pickupContactForm);
    this.setContactDetailsFromQueryParams(params.deliveryContact, this.dropoffContactForm);
  }

  private setContactDetailsFromQueryParams(
    contactQueryParams: B2cContactQueryParams,
    contactFormGroup: FormGroup,
  ) {
    const contactDetailsProvided = Object.values(contactQueryParams).some(
      (value) => value !== null,
    );

    if (contactDetailsProvided) {
      contactFormGroup.patchValue({
        forename: contactQueryParams.name,
        surname: contactQueryParams.surname,
        email: contactQueryParams.email,
        phone1: contactQueryParams.phone,
        notes: contactQueryParams.notes,
        isPrivate: contactQueryParams.isPrivate,
      });

      Object.values(contactFormGroup.controls).forEach((control) => control.markAsTouched());
    }
  }

  private async setAddressFromQueryParams(
    addressQueryParams: B2cAddressQueryParams,
    postcodeSearchResultAddresses: UnifiedAddressSuggestion[],
    isPickup: boolean,
  ) {
    const queryParamAddressLine = Object.values(addressQueryParams)
      .filter((value) => value != null)
      .join(', ');
    const matchedAddress = postcodeSearchResultAddresses.find(
      (a) => a.address === queryParamAddressLine,
    );

    if (matchedAddress) {
      await this.onSelectedAddressChange({ value: matchedAddress }, isPickup, false);

      const addressEvent = {
        addressLine1: this.editAddress.addressLine1,
        addressLine2: this.editAddress.addressLine2,
        addressLine3: this.editAddress.addressLine3,
        businessName: this.editAddress.businessName,
        city: this.editAddress.city,
        latitude: this.editAddress.latitude,
        longitude: this.editAddress.longitude,
        postcode: this.editAddress.postcode,
        primaryAddress: this.editAddress.primaryAddress,
      };

      this.addressChange(addressEvent);
    } else {
      isPickup ? (this.noMatchedPickupAddress = true) : (this.noMatchedDropoffAddress = true);
    }
  }
}

export function phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  const valid =
    /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$/.test(
      value,
    );
  return valid ? null : { notPhoneNumber: true };
}

export function emailValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as string;
  const valid = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value);
  return valid ? null : { notEmail: true };
}
